import React from "react";
// The design team's own tiles, pulled in as components so a className can size
// them. Each one draws its own tinted square and the glyph inside it, so
// nothing here has a fill or a colour of its own to keep in step with them.
import { ReactComponent as ReceivedIcon } from "../../../assets/svg/revenue/rupee.svg";
import { ReactComponent as PendingIcon } from "../../../assets/svg/revenue/Pending payment.svg";
import { ReactComponent as CancelledIcon } from "../../../assets/svg/revenue/Cancelled payment.svg";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { formatPrice } from "../../brand/planFormat";
import type { RevenueTotals } from "../types/revenue.types";

interface RevenueTileProps {
  /** The tile's glyph, square and all — see the imports above. */
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  label: string;
  amount: number;
  phase: LoadPhase;
  /** The list never arrived, so there is no figure — see below. */
  failed: boolean;
}

/**
 * One figure of the three. The tile carries the currency in its square, so the
 * figure beside it is written as a plain number — a second ₹ an inch away would
 * read as two.
 *
 * Square over the figure on a phone and beside it from `sm`, which is what fits
 * three of these across a phone's width — the same two shapes the dashboard's
 * welcome card takes with its own three highlights, so the two screens are not
 * laid out different ways at the same width.
 */
const RevenueTile: React.FC<RevenueTileProps> = ({
  Icon,
  label,
  amount,
  phase,
  failed,
}) => (
  <div className="flex flex-col items-center gap-1.5 px-2 py-4 text-center sm:flex-row sm:items-center sm:gap-3 sm:px-6 sm:text-left 3xl:py-5">
    {/* Sized on the wrapper rather than on the glyph, so the artwork keeps its
        own proportions whatever this is set to — and, on a phone, rounded off
        here too. Each file draws its tint as a rounded square filling its own
        box, so clipping the wrapper is what turns that square into a disc, and
        the three discs match the dashboard's highlight marks above them. The
        glyphs sit well inside the corners being cut, so nothing is lost.

        From `sm` the clip is squared off again, which hands each file back its
        own corner radius: the wider screen keeps the artwork as it was drawn. */}
    <span
      aria-hidden="true"
      className="h-9 w-9 sm:h-[42px] sm:w-[42px] 3xl:h-12 3xl:w-12 flex-shrink-0 overflow-hidden rounded-full sm:rounded-none"
    >
      <Icon className="w-full h-full" />
    </span>
    <div className="min-w-0 w-full sm:w-auto">
      {/* A total is a sum of rows that are not in yet, so while the list is out
          there is no figure to write — and a "0" here would read as a workspace
          that has taken nothing. A list that never arrived is the same: a dash,
          not a zero. The line is held at its own height either way, so nothing
          shifts when the number lands. */}
      {failed ? (
        <div className="flex h-[22px] sm:h-[27px] 3xl:h-[32px] items-center justify-center sm:justify-start text-[17px] sm:text-[22px] 3xl:text-[26px] font-medium text-ink-muted leading-tight">
          -
        </div>
      ) : phase === "ready" ? (
        <div
          className="text-[17px] sm:text-[22px] 3xl:text-[26px] font-medium text-ink leading-tight truncate"
          // What is written is the number; what is read out is the money, and
          // what the number is of.
          aria-label={`${formatPrice(amount)} ${label.toLowerCase()}`}
        >
          {amount.toLocaleString("en-IN")}
        </div>
      ) : (
        <div className="flex h-[22px] sm:h-[27px] 3xl:h-[32px] items-center justify-center sm:justify-start">
          <SkeletonCard
            phase={phase}
            className="h-4 w-[56px] sm:h-5 sm:w-[96px]"
          />
        </div>
      )}
      {/* Wrapping rather than truncated on a phone: a third of that width is
          not enough for "Total payment received", and a tile labelled "Total
          payme…" says less than one that takes a second line. From `sm` there
          is room for the line, and the ellipsis is the safer end. */}
      <p className="text-[11px] sm:text-[13px] 3xl:text-[14px] leading-tight text-ink-muted sm:leading-normal sm:truncate">
        {label}
      </p>
    </div>
  </div>
);

interface RevenueSummaryProps {
  totals: RevenueTotals;
  /**
   * Where the list is. The figures are a sum of it, so all three wait, shimmer
   * and land together — see `RevenueTile`.
   */
  phase?: LoadPhase;
  /** The list could not be loaded, so all three read as a dash rather than 0. */
  failed?: boolean;
}

/**
 * The three figures above the table: what has come in, what is still out, and
 * what was called off — each one measured over the period the screen is set to,
 * so a tile and the rows under it always cover the same stretch of time.
 *
 * Three across at every width, phones included: these are the screen's headline
 * and stacking them spends most of a phone's first screenful before the table
 * has been reached at all.
 *
 * One card, and on a phone one undivided card: at that width the three figures
 * are already close enough to read as a set, and two rules through them make
 * three narrow boxes out of what is a single band of the screen. The rules come
 * back from `sm`, where the tiles are wide enough to need telling apart.
 */
const RevenueSummary: React.FC<RevenueSummaryProps> = ({
  totals,
  phase = "ready",
  failed = false,
}) => (
  <section className="flex-shrink-0 grid grid-cols-3 divide-x-0 sm:divide-x divide-line card-surface border border-line bg-white">
    <RevenueTile
      Icon={ReceivedIcon}
      label="Total payment received"
      amount={totals.received}
      phase={phase}
      failed={failed}
    />
    <RevenueTile
      Icon={PendingIcon}
      label="Pending payment"
      amount={totals.pending}
      phase={phase}
      failed={failed}
    />
    <RevenueTile
      Icon={CancelledIcon}
      label="Cancelled payment"
      amount={totals.cancelled}
      phase={phase}
      failed={failed}
    />
  </section>
);

export default RevenueSummary;
